import React, { useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import {
  PayoutMethodType,
  UserPayoutMethod,
} from '../../../../../../types/Payout'
import { RevolutDetails } from '../../../../../../types/User/UserPayoutMethod/Revolut/RevolutDetails'
import userService from '../../../../../../services/user.service'
import { displayAddress } from '../../../../../../lib/utils/multiversx'
import { Tooltip } from 'react-tooltip'

interface PayoutMethodDisplayProps {
  userPayoutMethod: UserPayoutMethod
}

export default function PayoutMethodDisplay({
  userPayoutMethod,
}: PayoutMethodDisplayProps) {
  const { t } = useTranslation('dashboard')

  const [revolutDetails, setRevolutDetails] = useState<
    RevolutDetails | undefined
  >(undefined)

  const fetchRevolutDetails = async () => {
    const details = await userService.getRevolutCounterpartyIbanDetails(
      userPayoutMethod.id,
    )
    setRevolutDetails(details)
  }

  useEffect(() => {
    if (userPayoutMethod.userPayoutMethodRevolut) {
      fetchRevolutDetails()
    }
  }, [userPayoutMethod])

  const PayoutMethodTypeTranslations: Record<PayoutMethodType, string> = {
    [PayoutMethodType.MULTIVERSX_EURC]: t(
      'payoutsReview.tab.columns.payoutMethod.balance',
    ),
    [PayoutMethodType.MULTIVERSX_USDC]: t(
      'payoutsReview.tab.columns.payoutMethod.multiversX',
    ),
    [PayoutMethodType.REVOLUT_EUR]: t(
      'payoutsReview.tab.columns.payoutMethod.revolut',
    ),
    [PayoutMethodType.PAYPAL_EUR]: t(
      'payoutsReview.tab.columns.payoutMethod.paypal',
    ),
  }

  const PayoutMethodTypeImageSrc: Record<PayoutMethodType, string> = {
    [PayoutMethodType.MULTIVERSX_USDC]: '/img/multiversx.png',
    [PayoutMethodType.MULTIVERSX_EURC]: '/img/multiversx.png',
    [PayoutMethodType.REVOLUT_EUR]: '/img/brands/payouts/bank-min.png',
    [PayoutMethodType.PAYPAL_EUR]: '/img/brands/payouts/paypal-min.png',
  }

  const PayoutMethodTypeAssociatedAccount: Record<
    PayoutMethodType,
    React.JSX.Element
  > = {
    [PayoutMethodType.MULTIVERSX_USDC]:
      userPayoutMethod.userPayoutMethodMultiversx ? (
        <p
          className="font-semibold text-neutralBlack/80 hover:cursor-pointer"
          data-tooltip-id={
            'mvxAddressTooltip-' +
            userPayoutMethod.userPayoutMethodMultiversx.id
          }
        >
          {displayAddress(userPayoutMethod.userPayoutMethodMultiversx.address)}
          <Tooltip
            place="bottom"
            id={
              'mvxAddressTooltip-' +
              userPayoutMethod.userPayoutMethodMultiversx.id
            }
          >
            {userPayoutMethod.userPayoutMethodMultiversx.address}
          </Tooltip>
        </p>
      ) : (
        <>N/A</>
      ),
    [PayoutMethodType.MULTIVERSX_EURC]:
      userPayoutMethod.userPayoutMethodMultiversx ? (
        <p
          className="font-semibold text-neutralBlack/80 hover:cursor-pointer"
          data-tooltip-id={
            'mvxAddressTooltip-' +
            userPayoutMethod.userPayoutMethodMultiversx.id
          }
        >
          {displayAddress(userPayoutMethod.userPayoutMethodMultiversx.address)}
          <Tooltip
            place="bottom"
            id={
              'mvxAddressTooltip-' +
              userPayoutMethod.userPayoutMethodMultiversx.id
            }
          >
            {userPayoutMethod.userPayoutMethodMultiversx.address}
          </Tooltip>
        </p>
      ) : (
        <>N/A</>
      ),
    [PayoutMethodType.REVOLUT_EUR]:
      revolutDetails && revolutDetails.redactedIban ? (
        <>{revolutDetails.redactedIban}</>
      ) : (
        <>N/A</>
      ),
    [PayoutMethodType.PAYPAL_EUR]: userPayoutMethod.userPayoutMethodPaypal ? (
      <>{userPayoutMethod.userPayoutMethodPaypal.email}</>
    ) : (
      <>N/A</>
    ),
  }

  return (
    <div>
      <div className="flex items-center space-x-3">
        <img
          alt="bank icon"
          src={PayoutMethodTypeImageSrc[userPayoutMethod.payoutMethod.type]}
          loading={'lazy'}
          className="aspect-square h-10"
        />

        <div>
          <div>
            <p className="text-gray-700">
              {PayoutMethodTypeTranslations[userPayoutMethod.payoutMethod.type]}
            </p>

            <div className="relative flex-grow items-center text-sm sm:flex sm:space-x-1">
              <div className="font-light text-neutralBlack">
                {t('payoutsReview.tabDetails.associatedAccount.text')}
              </div>
              <p className="truncate font-semibold text-neutralBlack/80">
                {
                  PayoutMethodTypeAssociatedAccount[
                    userPayoutMethod.payoutMethod.type
                  ]
                }
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
